import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRef, useEffect } from "react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_da037825-faa5-420e-a579-84f9393da584/artifacts/qxg7ev86_redeemlogo.jpeg";
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/da037825-faa5-420e-a579-84f9393da584/images/ccbeeb07a8eb5159ba4fe31fdb563cd9c1d7b98c6ab1b5ea2228438894eca106.png";
const HERO_VIDEO = `${process.env.REACT_APP_BACKEND_URL}/api/media/hero-video`;

export default function HeroSection() {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // Force muted as DOM property (critical for mobile autoplay)
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute("muted", "");
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");
    // Remove controls completely
    v.controls = false;
    v.removeAttribute("controls");
    
    const tryPlay = () => {
      v.muted = true;
      v.controls = false;
      const p = v.play();
      if (p) p.catch(() => {});
    };
    
    tryPlay();
    // Keep retrying every 500ms until playing
    const retryInterval = setInterval(() => {
      if (v.paused) tryPlay();
      else clearInterval(retryInterval);
    }, 500);
    // Also try on any user interaction
    const playOnInteract = () => { tryPlay(); clearInterval(retryInterval); };
    document.addEventListener("touchstart", playOnInteract, { once: true });
    document.addEventListener("click", playOnInteract, { once: true });
    document.addEventListener("scroll", playOnInteract, { once: true });
    return () => {
      clearInterval(retryInterval);
      document.removeEventListener("touchstart", playOnInteract);
      document.removeEventListener("click", playOnInteract);
      document.removeEventListener("scroll", playOnInteract);
    };
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Fallback static image (shows while video loads) */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      {/* Animated video background */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        controls={false}
        preload="auto"
        src={HERO_VIDEO}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ pointerEvents: "none", WebkitAppearance: "none" }}
        webkit-playsinline="true"
      />
      {/* Overlay */}
      <div className="absolute inset-0 bg-navy/40" />

      {/* Animated floating orbs */}
      <motion.div
        className="absolute top-20 left-10 w-32 h-32 rounded-full bg-cgreen/20 blur-3xl"
        animate={{ y: [0, -30, 0], x: [0, 15, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-32 right-20 w-48 h-48 rounded-full bg-crimson/15 blur-3xl"
        animate={{ y: [0, 20, 0], x: [0, -20, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute top-1/3 right-1/4 w-24 h-24 rounded-full bg-white/10 blur-2xl"
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <img
            src={LOGO_URL}
            alt="RCCG Dominion Parish"
            className="w-28 h-28 sm:w-36 sm:h-36 rounded-full mx-auto shadow-2xl border-4 border-white/30 mb-8"
            data-testid="hero-logo"
          />
        </motion.div>

        <motion.p
          className="text-white font-body font-bold text-sm sm:text-base uppercase tracking-[0.25em] mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.9, ease: [0.25, 0.1, 0.25, 1] }}
        >
          The Redeemed Christian Church of God
        </motion.p>

        <motion.h1
          className="font-heading font-black text-4xl sm:text-5xl lg:text-7xl text-white leading-tight mb-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
        >
          Dominion Parish
        </motion.h1>

        <motion.p
          className="font-body text-white/80 text-base sm:text-lg max-w-2xl mx-auto mb-4 leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.9, ease: [0.25, 0.1, 0.25, 1] }}
        >
          Felixstowe
        </motion.p>

        <motion.div
          className="flex flex-wrap items-center justify-center gap-3 mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.9, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <span className="px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-body font-semibold border border-white/20">
            Faith
          </span>
          <span className="px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-body font-semibold border border-white/20">
            Power
          </span>
          <span className="px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-body font-semibold border border-white/20">
            Dominion
          </span>
        </motion.div>

        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <Button
              data-testid="hero-join-btn"
              onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="rounded-full bg-cgreen hover:bg-cgreen/90 text-white px-8 py-6 text-base font-body font-bold shadow-lg shadow-cgreen/30 transition-all hover:-translate-y-0.5 cursor-pointer"
            >
              Join Us This Sunday
            </Button>
            <Button
              data-testid="hero-learn-btn"
              onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              variant="outline"
              className="rounded-full border-2 border-white/40 text-white px-8 py-6 text-base font-body font-bold hover:bg-white/10 backdrop-blur-sm transition-all cursor-pointer"
            >
              Learn More
            </Button>
        </motion.div>

        {/* Scroll indicator */}
        <motion.button
          onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth", block: "start" })}
          className="mt-10 inline-block cursor-pointer"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="w-8 h-8 text-white/50" />
        </motion.button>
      </div>
    </section>
  );
}
